import { AnimatePresence, motion } from 'framer-motion';
import { WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { springGentle } from '../tokens';

/**
 * Top-anchored "You're offline" banner. Slides down from the safe-area
 * top when navigator goes offline and slides back up when connectivity
 * returns. Sits above the rest of the chrome (z-50) but below toasts
 * (z-60) so toast confirmations still stack on top.
 *
 * Doesn't ship a "Retry" affordance — Margin's free tier is fully
 * offline-capable, so being offline isn't broken, it's just worth
 * acknowledging so the user knows cloud sync is paused.
 */
export function OfflineBanner() {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return (
    <AnimatePresence>
      {!online ? (
        <motion.div
          key="offline"
          role="status"
          aria-live="polite"
          initial={{ y: '-120%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '-120%', opacity: 0 }}
          transition={springGentle}
          style={{ willChange: 'transform' }}
          className="fixed inset-x-0 top-0 z-50 px-screen-x pt-[max(env(safe-area-inset-top),12px)] pb-2 pointer-events-none"
        >
          <div className="mx-auto max-w-content rounded-pill bg-text-primary text-bg shadow-lg px-4 py-2 inline-flex items-center gap-2 pointer-events-auto">
            <WifiOff size={14} strokeWidth={1.8} />
            <span className="text-[13px] font-medium">You’re offline</span>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
